import React, { useEffect, useState, useMemo } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import Select from 'react-select'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import { FaSearch, FaChevronLeft, FaChevronRight, FaEdit, FaTrash, FaEye, FaFilePdf } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import '../../HR Manager/screens/Employees.css'
import API_BASE_URL from '../../../config/api'
import Swal from 'sweetalert2'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'


const DepartmentEmployees = () => {
  const baseUrl = API_BASE_URL
  const [employees, setEmployees] = useState([])
  const [departments, setDepartments] = useState([])
  const [costCenters, setCostCenters] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState(null)
  const [selectedCostCenter, setSelectedCostCenter] = useState(null)
  const [page, setPage] = useState(1)
  const [rowsPerPage] = useState(20)
    const [deletingEmployeeId, setDeletingEmployeeId] = useState(null)
  

  const fetchAll = async () => {
    setLoading(true)
    try {
      const token = Cookies.get('token')
      const [empRes, depRes, ccRes] = await Promise.all([
        axios.get(`${baseUrl}/api/employees/`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${baseUrl}/api/departments/`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${baseUrl}/api/course-centers/`, { headers: { 'Authorization': `Bearer ${token}` } })
      ])
      setEmployees(empRes.data)
      setDepartments(depRes.data)
      setCostCenters(ccRes.data)
    } catch (err) {
      console.error('Failed to fetch employees:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const departmentOptions = useMemo(() => departments.map(dep => ({
    value: dep.id,
    label: dep.course_center_name ? `${dep.name} (${dep.course_center_name})` : dep.name
  })), [departments])

  const costCenterOptions = useMemo(() => costCenters.map(cc => ({
    value: cc.id,
    label: cc.name
  })), [costCenters])

  // Filtering logic
  const filteredEmployees = useMemo(() => {
    let filtered = employees

    if (search) {
      const s = search.toLowerCase()
      filtered = filtered.filter(emp =>
        emp.first_name?.toLowerCase().includes(s) ||
        emp.last_name?.toLowerCase().includes(s) ||
        emp.national_id?.toLowerCase().includes(s) ||
        emp.position?.toLowerCase().includes(s)
      )
    }
    if (selectedDepartment) {
      filtered = filtered.filter(emp => emp.department === selectedDepartment.value)
    }
    if (selectedCostCenter) {
      // Find departments with this cost center
      const depIds = departments.filter(dep => dep.course_center === selectedCostCenter.value).map(dep => dep.id)
      filtered = filtered.filter(emp => depIds.includes(emp.department))
    }
    return filtered
  }, [employees, search, selectedDepartment, selectedCostCenter, departments])

  // Pagination logic
  const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage)
  const paginatedEmployees = filteredEmployees.slice((page - 1) * rowsPerPage, page * rowsPerPage)

  const handleSearchChange = e => {
    setSearch(e.target.value)
    setPage(1)
  }
  const handleDepartmentChange = val => {
    setSelectedDepartment(val)
    setPage(1)
  }
  const handleCostCenterChange = val => {
    setSelectedCostCenter(val)
    setPage(1)
  }
  const handlePrevPage = () => setPage(p => Math.max(1, p - 1))
  const handleNextPage = () => setPage(p => Math.min(totalPages, p + 1))

  const handleDeleteEmployee = async (employeeId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete this employee. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    })

    if (!result.isConfirmed) return

    setDeletingEmployeeId(employeeId)
    try {
      const token = Cookies.get('token')
      const response = await axios.delete(`${baseUrl}/api/employees/${employeeId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      // Validate that the server actually deleted the record (expect 200 or 204)
      if (response.status !== 200 && response.status !== 204) {
        throw new Error(`Unexpected response status: ${response.status}`)
      }

      // Re-fetch from server to reflect true backend state
      await fetchAll()
      Swal.fire('Deleted!', 'The employee has been deleted.', 'success')
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to delete employee'
      Swal.fire('Error', message, 'error')
      // Re-fetch in case local state is out of sync
      await fetchAll()
    } finally {
      setDeletingEmployeeId(null)
    }
  }

  const handleExportDepartmentEmployeesPdf = () => {
    if (!filteredEmployees.length) {
      Swal.fire('No data', 'No employees available to export.', 'info')
      return
    }

    const doc = new jsPDF('p', 'mm', 'a4')
    doc.setFontSize(16)
    doc.text('Department Employees Report', 14, 15)
    doc.setFontSize(10)
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22)

    const tableRows = filteredEmployees.map((emp, index) => {
      const department = departments.find(dep => dep.id === emp.department)
      const costCenter = department
        ? costCenters.find(cc => cc.id === department.course_center)
        : null

      return [
        index + 1,
        emp.first_name || 'N/A',
        emp.last_name || 'N/A',
        emp.national_id || 'N/A',
        emp.position || 'N/A',
        department?.name || 'N/A',
        costCenter?.name || 'N/A',
        `${(emp.performance_progress || 0).toFixed(2)}%`
      ]
    })

    autoTable(doc, {
      head: [['#', 'Name', 'Surname', 'National ID', 'Position', 'Department', 'Cost Center', 'Progress']],
      body: tableRows,
      startY: 28,
      styles: { fontSize: 8.5, cellPadding: 2.5 },
      headStyles: { fillColor: [30, 64, 175] }
    })

    doc.save(`department-employees-${new Date().toISOString().slice(0, 10)}.pdf`)
    Swal.fire('Success', 'Department employees PDF generated.', 'success')
  }

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employees-container">
        <div className="page-header">
          <div className="header-content">
            <div className="page-title">
              <h1>Employees</h1>
              <p>View and manage all employees in your department</p>
            </div>
          </div>
        </div>
        <div className="hr-content-wrapper">
          <div className="filters-bar">
            <div className="search-bar">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by name, national ID, or position..."
                value={search}
                onChange={handleSearchChange}
                className="search-input"
                disabled={loading}
                style={{ width: '40%', border: '2px solid #e2e8f0', padding: '10px', borderRadius: '10px' }}
              />
            </div>
            <button
              type="button"
              onClick={handleExportDepartmentEmployeesPdf}
              className="review-button"
              disabled={loading || filteredEmployees.length === 0}
              title="Export department employees as PDF"
            >
              <FaFilePdf style={{ marginRight: '6px' }} />
              Export PDF
            </button>

          </div>
          <div className="table-wrapper">
            <table className="employees-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Surname</th>
                  <th>National Id</th>
                  <th>Position</th>
                  <th>Progress</th>
                  <th>Actions</th>


                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="loading-row">
                      <div className="beat-loader">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedEmployees.length === 0 ? (
                  <tr><td colSpan={5} className="empty-row">No employees found.</td></tr>
                ) : (
                  paginatedEmployees.map(emp => {
                    return (
                      <tr key={emp.id}>
                        <td>{emp.first_name}</td>
                        <td>{emp.last_name}</td>
                        <td>{emp.national_id}</td>
                        <td>{emp.position}</td>
                        <td>
                          <div className="progress-bar-container">
                            <div className="progress-bar-bg" style={{ position: 'relative' }}>
                              <div
                                className="progress-bar-fill"
                                style={{ backgroundColor: `${emp.performance_indicator}`, width: `${(emp.performance_progress || 0).toFixed(2)}%` }}
                              >
                                <span className="progress-bar-label-inside">
                                  {(emp.performance_progress || 0).toFixed(2)}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Link
                            to={{
                              pathname: `/hod/employee/${emp.id}/goals`,
                            }}
                            state={{ employeeId: emp.id }}
                            style={{ textDecoration: 'none' }}
                          >
                            <button className="action-button view-button" title="View">
                              <FaEye />
                            </button>
                          </Link>
                          <Link
                            to={{
                              pathname: `/hod/update-employee/${emp.id}`,
                            }}
                            state={{ employeeId: emp.id }}
                            style={{ textDecoration: 'none' }}
                          >
                            <button className="action-button edit-button" title="Edit">
                              <FaEdit />
                            </button>
                          </Link>
                          <button
                            className="action-button delete-button"
                            title="Delete"
                            onClick={() => handleDeleteEmployee(emp.id)}
                            disabled={deletingEmployeeId === emp.id}
                          >
                            <FaTrash />
                          </button>
                          <Link
                            to={{
                              pathname: `/hod/employee-goals/${emp.id}/reviews`,
                            }}
                            state={{ employeeId: emp.id }}
                            style={{ textDecoration: 'none' }}
                          >
                            <button className='review-button' title="View">
                              Review KPIs
                            </button>
                          </Link>

                        </td>



                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="pagination-bar">
            <button onClick={handlePrevPage} disabled={page === 1} className="pagination-btn"><FaChevronLeft /></button>
            <span className="pagination-info">Page {page} of {totalPages || 1}</span>
            <button onClick={handleNextPage} disabled={page === totalPages || totalPages === 0} className="pagination-btn"><FaChevronRight /></button>
          </div>
        </div>
      </div>
    </>
  )
}

export default DepartmentEmployees
